import { TranslateService } from '@ngx-translate/core';
import {
  AuthConfig,
  OAuthEvent,
  OAuthService,
  OAuthSuccessEvent,
} from 'angular-oauth2-oidc';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import { lastValueFrom, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { LoginState } from '~/auth/interface/login-state.interface';
import { UserService } from '~/auth/services/user/user.service';

export const AUTH_ISSUER_KEY = 'auth_iss';

export abstract class AbstractAuthProvider {
  protected constructor(
    protected readonly oAuthService: OAuthService,
    protected readonly userService: UserService,
    protected readonly config: AuthConfig,
    protected readonly discoveryDocument: string,
    protected readonly storage: Storage,
    protected readonly translateService: TranslateService
  ) {}

  protected abstract getScopes(): Observable<string[]>;

  /**
   * called if we need to log in the user after provider configuration
   */
  public abstract tryLogin(): Promise<boolean>;

  /**
   * handle redirection after login success
   */
  protected processState(state?: string): void {
    let parsedState: LoginState = {
      lang: this.translateService.currentLang,
      from: '/',
    };

    if (state) {
      parsedState = JSON.parse(state);
    }

    // wait for user logged-in
    // and emit login state
    this.userService.isAuthenticated().subscribe((isLogged) => {
      if (isLogged) {
        this.userService.loggedIn$.next(parsedState);
      }
    });
  }

  /**
   * common auth provider configuration
   */
  public async configure(): Promise<OAuthSuccessEvent> {
    // events handling
    this.oAuthService.events.subscribe((e) => {
      this.logoutEvent(e);
      this.tokenReceivedEvent(e);
    });

    const auth = this.getScopes().pipe(
      tap((scopes) => {
        this.oAuthService.tokenValidationHandler = new JwksValidationHandler();
        this.oAuthService.configure({
          clearHashAfterLogin: false,
          ...this.config,
          scope: scopes.join(' '),
          strictDiscoveryDocumentValidation: false,
          requireHttps: false,
          // To use to debug refresh token with Angular OIDC.
          // timeoutFactor: 0.01, // 0.01 => With Connect: 9s; With FED: 72s.
          // silentRefreshShowIFrame: true,
        });

        this.oAuthService.setupAutomaticSilentRefresh();
      })
    );

    await lastValueFrom(auth);
    return await // load discovery document and try to log already authenticated user
    this.oAuthService.loadDiscoveryDocument(this.discoveryDocument);
  }

  public getIssuer(): string {
    return this.config.issuer;
  }

  public getOAuthService(): OAuthService {
    return this.oAuthService;
  }

  /**
   * is logged
   */
  public isAuthenticated(): boolean {
    return this.oAuthService.hasValidAccessToken();
  }

  /**
   * login using code or implicit flow
   */
  public async login(additionalState?: LoginState): Promise<OAuthSuccessEvent> {
    const event = await this.configure();
    this.storage.setItem(AUTH_ISSUER_KEY, this.config.issuer);
    this.oAuthService.initLoginFlow(JSON.stringify(additionalState));
    return event;
  }

  public logout(): Promise<OAuthSuccessEvent> {
    this.oAuthService.logOut();
    this.storage.removeItem(AUTH_ISSUER_KEY);

    return this.oAuthService.revokeTokenAndLogout();
  }

  /**
   * EVENTS
   */
  public logoutEvent(e: OAuthEvent): void {
    if (e.type === 'logout') {
      this.userService.setUser();
    }
  }

  public tokenReceivedEvent(e: OAuthEvent): void {
    if (e.type === 'token_received') {
      if (this.userService.getUser()) {
        this.userService
          .updateUserTokens(
            this.oAuthService.getAccessToken(),
            this.oAuthService.getRefreshToken()
          )
          .subscribe();
      }
    }
  }
}
